"use client";

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { fireDB } from "../../../Backend/Firebase/FirebaseConfigData";
import { toast, ToastContainer } from "react-toastify";
import SummernoteEditor from "./SummernoteEditor";
import { motion, AnimatePresence } from "framer-motion";   // ⭐ Added for animations
import "react-toastify/dist/ReactToastify.css";

interface ChapterData {
  chapterSlug: string;
  chapterSlugManual?: boolean;
  contentTitle: string;
  title: string;
  titleManual?: boolean;
  content: string;
  author: string;
  seoTitle: string;
  seoDescription: string;
  seoTags: string;
  seoHeading: string;
  seoHeadingManual?: boolean;
  canonicalUrl: string;
  createdAt: string;
  sections?: SectionData[];
}

interface SectionData {
  sectionTitle: string;
  content: string;
}

export default function CreateChapter() {
  const { librarySlug, bookSlug } = useParams();

  const [chapterData, setChapterData] = useState<ChapterData>({
    chapterSlug: "",
    contentTitle: "",
    title: "",
    content: "",
    author: "",
    seoTitle: "",
    seoDescription: "",
    seoTags: "",
    seoHeading: "",
    canonicalUrl: "",
    createdAt: new Date().toISOString(),
    sections: [],
  });

  // Auto-generate slug, SEO, canonical URL
  useEffect(() => {
    if (chapterData.contentTitle) {
      const slugified = chapterData.contentTitle.toLowerCase().trim().replace(/\s+/g, "-");

      setChapterData((prev) => ({
        ...prev,
        chapterSlug: prev.chapterSlugManual ? prev.chapterSlug : slugified,
        title: prev.titleManual ? prev.title : prev.contentTitle,
        seoHeading: prev.seoHeadingManual ? prev.seoHeading : prev.contentTitle,
        canonicalUrl: `https://forumde.in/library/${librarySlug}/${bookSlug}/${slugified}`,
      }));
    }
  }, [chapterData.contentTitle]);

  // Add a new section
  const addSection = () => {
    setChapterData(prev => ({
      ...prev,
      sections: [...(prev.sections || []), { sectionTitle: "", content: "" }]
    }));
  };

  // Update section field
  const updateSection = (index: number, key: keyof SectionData, value: string) => {
    setChapterData(prev => {
      const updatedSections = [...(prev.sections || [])];
      updatedSections[index] = { ...updatedSections[index], [key]: value };
      return { ...prev, sections: updatedSections };
    });
  };

  // Remove section with animation
  const removeSection = (index: number) => {
    setChapterData(prev => ({
      ...prev,
      sections: prev.sections?.filter((_, i) => i !== index)
    }));
  };

  // Save chapter
  const handleSubmit = async () => {
    if (!librarySlug || !bookSlug) {
      toast.error("Missing librarySlug or bookSlug!", { position: "top-center" });
      return;
    }

    if (!chapterData.chapterSlug || !chapterData.contentTitle) {
      toast.error("Please fill all required fields!", { position: "top-center" });
      return;
    }

    try {
      await setDoc(
        doc(fireDB, "NewBlogs", librarySlug, "books", bookSlug, "chapters", chapterData.chapterSlug),
        { ...chapterData, time: Timestamp.now() }
      );

      toast.success("Chapter created successfully!", { position: "top-center" });

      // Reset form
      setChapterData({
        chapterSlug: "",
        contentTitle: "",
        title: "",
        content: "",
        author: "",
        seoTitle: "",
        seoDescription: "",
        seoTags: "",
        seoHeading: "",
        canonicalUrl: "",
        createdAt: new Date().toISOString(),
        sections: [],
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      toast.error("Error creating chapter", { position: "top-center" });
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen py-10 px-6">
      <div className="max-w-5xl mx-auto space-y-8">

        <motion.h1 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-bold text-center mb-6 text-gray-800"
        >
          Create New Chapter
        </motion.h1>

        {/* Chapter Slug + Title + Author */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-r from-red-100 to-blue-100 p-6 rounded-2xl shadow-lg border flex flex-col md:flex-row gap-6"
        >
          <div className="flex-1 space-y-3">
            <label className="block font-semibold">Content Title</label>
            <input
              value={chapterData.contentTitle}
              onChange={(e) => setChapterData({ ...chapterData, contentTitle: e.target.value })}
              className="w-full border p-3 rounded-lg shadow-sm focus:ring-2 focus:ring-red-400"
            />

            <label className="block font-semibold">Chapter Slug</label>
            <input
              value={chapterData.chapterSlug}
              onChange={(e) =>
                setChapterData({ ...chapterData, chapterSlug: e.target.value, chapterSlugManual: true })
              }
              className="w-full border p-3 rounded-lg shadow-sm focus:ring-2 focus:ring-red-400"
            />

            <label className="block font-semibold">Author</label>
            <input
              value={chapterData.author}
              onChange={(e) => setChapterData({ ...chapterData, author: e.target.value })}
              className="w-full border p-3 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </motion.div>

        {/* Main Content Editor */}
        {/* <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-green-100 p-6 rounded-2xl shadow-lg border border-green-300"
        >
          <label className="block font-semibold mb-2">Main Content</label>
          <SummernoteEditor
            editorId="main-chapter"
            initialContent={chapterData.content}
            onChange={(content) => setChapterData(prev => ({ ...prev, content }))}
          />
        </motion.div> */}

        {/* Sections */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-yellow-50 p-6 rounded-2xl shadow-lg space-y-6 border border-yellow-200"
        >
          <h2 className="text-3xl font-semibold mb-4 text-yellow-800">Sections</h2>

          {/* Animated list */}
          <AnimatePresence>
            {(chapterData.sections || []).map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, x: -50, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="bg-white p-5 rounded-2xl shadow border space-y-4 hover:shadow-md transition-all"
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Section {index + 1}</h3>
                  <button
                    onClick={() => removeSection(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    ❌ Remove
                  </button>
                </div>

                <input
                  placeholder="Section Title"
                  value={section.sectionTitle}
                  onChange={(e) => updateSection(index, "sectionTitle", e.target.value)}
                  className="w-full border p-3 rounded-lg"
                />

                <SummernoteEditor
                  editorId={`section-${index}`}
                  initialContent={section.content}
                  onChange={(content) => updateSection(index, "content", content)}
                />
              </motion.div>
            ))}
          </AnimatePresence>

          <motion.button
            whileTap={{ scale: 0.93 }}
            whileHover={{ scale: 1.03 }}
            onClick={addSection}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl shadow hover:bg-blue-700 transition-all"
          >
            ➕ Add Section
          </motion.button>
        </motion.div>

        {/* SEO Fields */}
        <div className="bg-gray-200 p-6 rounded-2xl shadow space-y-4">
          {["seoTitle", "seoDescription", "seoTags", "seoHeading", "canonicalUrl"].map((field) => (
            <div key={field} className="space-y-1">
              <label className="block font-medium">
                {field === "canonicalUrl" ? "Canonical URL (auto-generated)" : field.replace(/seo/i, "SEO ")}
              </label>
              {field === "seoDescription" ? (
                <textarea
                  placeholder={`Enter ${field}`}
                  className="w-full border p-3 rounded-lg shadow-sm focus:ring-2 focus:ring-gray-400"
                  value={chapterData[field as keyof ChapterData] ?? ""}
                  onChange={(e) => setChapterData({ ...chapterData, [field]: e.target.value })}
                />
              ) : (
                <input
                  placeholder={`Enter ${field}`}
                  readOnly={field === "canonicalUrl"}
                  className={`w-full border p-3 rounded-lg shadow-sm focus:ring-2 focus:ring-gray-400 ${field === "canonicalUrl" ? "bg-gray-100" : ""}`}
                  value={chapterData[field as keyof ChapterData] ?? ""}
                  onChange={(e) => setChapterData({ ...chapterData, [field]: e.target.value })}
                />
              )}
            </div>
          ))}
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSubmit}
          className="w-full bg-green-600 text-white p-4 rounded-lg text-xl hover:bg-green-700 transition-all"
        >
          ✅ Submit Chapter
        </motion.button>

      </div>

      <ToastContainer position="top-center" />
    </div>
  );
}
